import { getImpersonatedPersonId } from "../local-storage/impersonated-person";
import { getPreferredLanguage } from "../local-storage/language";
import { getOrganizationId } from "../local-storage/selected-organization";
import type { WireMessage } from "./agentMemory";

export type WireTool = { name: string; description: string; parameters: string };

export type StreamedToolCall = { id: string; name: string; arguments: string };

export type StreamRequest = { messages: WireMessage[]; tools?: WireTool[]; model?: string };

export type StreamOutcome = {
  text: string;
  toolCalls: StreamedToolCall[];
  finishReason: "stop" | "toolCalls";
  errored: boolean;
  errorCode: string | null;
};

export type StreamHandlers = {
  onDelta?: (accumulated: string) => void;
  onToolCallStart?: (name: string) => void;
};

export type StreamOptions = { timeoutMs: number; signal?: AbortSignal };

export class AgentStreamHttpError extends Error {
  constructor(
    readonly status: number,
    readonly errorCode: string | null,
  ) {
    super(`agent stream request failed: ${status}${errorCode ? ` (${errorCode})` : ""}`);
  }
}

type StreamEventLine = {
  type: "delta" | "toolCallStart" | "toolCall" | "done" | "error";
  text?: string;
  name?: string;
  id?: string;
  arguments?: string;
  finishReason?: string;
  code?: string;
};

// The one reader for every assistant model stream (planning, answer, drafts). Mirrors the auth
// recipe of RTK's base query (cookie via include, organizationId/personId/preferred-language from
// local storage). Once the stream has opened it never rejects: a timeout, network drop or error
// event resolves with whatever arrived and errored: true — degradation is the caller's decision.
export const streamAgentTurn = async (
  request: StreamRequest,
  handlers: StreamHandlers,
  options: StreamOptions,
  fetchImpl: typeof fetch = fetch,
): Promise<StreamOutcome> => {
  const response = await fetchImpl(`${import.meta.env.VITE_BFF_BASE_URL}/api/buyer/agent/stream`, {
    method: "POST",
    credentials: "include",
    headers: streamHeaders(),
    body: JSON.stringify(request),
    signal: options.signal,
  });
  if (!response.ok || !response.body) {
    throw new AgentStreamHttpError(response.status, await errorCodeOf(response));
  }

  const state: StreamOutcome = { text: "", toolCalls: [], finishReason: "stop", errored: false, errorCode: null };
  const abort = new AbortController();
  const timeout = setTimeout(() => {
    state.errored = true;
    abort.abort();
  }, options.timeoutMs);

  const applyLine = (line: string): void => {
    if (line.trim()) {
      applyEvent(parseEvent(line), state, handlers);
    }
  };

  try {
    await response.body
      .pipeThrough(new TextDecoderStream())
      .pipeThrough(newlineSplitter())
      .pipeTo(new WritableStream({ write: applyLine }), { signal: abort.signal });
  } catch {
    state.errored = true;
  } finally {
    clearTimeout(timeout);
  }
  return state;
};

const newlineSplitter = (): TransformStream<string, string> => {
  const partial = { line: "" };
  return new TransformStream({
    transform: (chunk, controller) => {
      const lines = (partial.line + chunk).split("\n");
      partial.line = lines[lines.length - 1];
      lines.slice(0, -1).forEach((line) => controller.enqueue(line));
    },
    flush: (controller) => {
      controller.enqueue(partial.line);
    },
  });
};

const streamHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    "preferred-language": getPreferredLanguage(),
  };
  const organizationId = getOrganizationId();
  if (organizationId) {
    headers.organizationId = organizationId;
  }
  const impersonatedPersonId = getImpersonatedPersonId();
  if (impersonatedPersonId) {
    headers.personId = impersonatedPersonId;
  }
  return headers;
};

const parseEvent = (line: string): StreamEventLine | null => {
  try {
    return JSON.parse(line) as StreamEventLine;
  } catch {
    return null;
  }
};

const applyEvent = (event: StreamEventLine | null, state: StreamOutcome, handlers: StreamHandlers): void => {
  if (!event) {
    return;
  }
  switch (event.type) {
    case "delta": {
      state.text += event.text ?? "";
      handlers.onDelta?.(state.text);
      break;
    }
    case "toolCallStart": {
      if (event.name) {
        handlers.onToolCallStart?.(event.name);
      }
      break;
    }
    case "toolCall": {
      state.toolCalls.push({ id: event.id ?? "", name: event.name ?? "", arguments: event.arguments ?? "" });
      break;
    }
    case "done": {
      state.finishReason = event.finishReason === "tool_calls" ? "toolCalls" : "stop";
      break;
    }
    case "error": {
      state.errored = true;
      state.errorCode = event.code ?? null;
      break;
    }
  }
};

const errorCodeOf = async (response: Response): Promise<string | null> => {
  try {
    const body = (await response.json()) as { errorCode?: string } | null;
    return body?.errorCode ?? null;
  } catch {
    return null;
  }
};
